import { isFuseHolderVariant } from "../auto-fuse";
import type { DefLookup } from "../catalog";
import type { ComponentDefinition, ComponentNode, Issue, System } from "../types";
import { buildGraph, type Edge, type Vertex, v } from "./graph";

type Panel = { node: ComponentNode; def: ComponentDefinition };
type PanelTerminal = { panel: Panel; positive: boolean };
type Fuse = { rating: number; ends: Vertex[]; cuts: (edge: Edge) => boolean; cableIds: string[]; nodeIds: string[] };

/** Check a module's explicit maximum against its individual string fuse.
 * Copper regions connect series modules only where exactly one positive meets
 * one negative. Cutting a fuse then distinguishes a string branch from a shared
 * combiner trunk, without treating a panel as a copper short across the array. */
export function detectPvStringFuseExceedsModule(system: System, defLookup: DefLookup): Issue[] {
  const panels: Panel[] = [];
  const panelTerminals = new Map<Vertex, PanelTerminal>();
  const converterTerminals = new Set<Vertex>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    if (def.category === "charger") for (const terminal of def.terminals) converterTerminals.add(v(node.id, terminal.id));
    if (def.category !== "solar") continue;
    const panel = { node, def };
    panels.push(panel);
    for (const terminal of def.terminals) {
      if (terminal.polarity === "positive" || terminal.polarity === "negative") {
        panelTerminals.set(v(node.id, terminal.id), { panel, positive: terminal.polarity === "positive" });
      }
    }
  }
  const limitOf = (panel: Panel) => {
    const value = panel.def.pvMaxSeriesFuseA;
    return value != null && Number.isFinite(value) && value > 0 ? value : Infinity;
  };
  if (!panels.some(panel => limitOf(panel) < Infinity)) return [];
  const graph = buildGraph(system, defLookup, { excludeSense: true });
  const region = (seed: Vertex, cuts: (edge: Edge) => boolean = () => false) => {
    const seen = new Set([seed]);
    const queue = [seed];
    for (let i = 0; i < queue.length; i++) for (const { vertex, via } of graph.adj.get(queue[i]) ?? []) {
      if (seen.has(vertex) || cuts(via)) continue;
      seen.add(vertex);
      queue.push(vertex);
    }
    return seen;
  };
  const terminalsIn = (vertices: Set<Vertex>) => [...vertices].flatMap(vertex => {
    const terminal = panelTerminals.get(vertex);
    return terminal ? [terminal] : [];
  });
  const series = new Map<Panel, Set<Panel>>(panels.map(panel => [panel, new Set()]));
  const visited = new Set<Vertex>();
  for (const seed of panelTerminals.keys()) {
    if (visited.has(seed)) continue;
    const vertices = region(seed);
    for (const vertex of vertices) visited.add(vertex);
    if ([...vertices].some(vertex => converterTerminals.has(vertex))) continue;
    const terminals = terminalsIn(vertices);
    if (terminals.length !== 2 || terminals[0].positive === terminals[1].positive) continue;
    const [a, b] = terminals.map(terminal => terminal.panel);
    if (a === b) continue;
    series.get(a)!.add(b);
    series.get(b)!.add(a);
  }

  const fuses: Fuse[] = [];
  const holders = new Map<string, number>();
  for (const node of system.nodes) {
    if (node.kind !== "component" || !(node.fuseAmp != null && node.fuseAmp > 0)) continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || !isFuseHolderVariant(def)) continue;
    holders.set(node.id, node.fuseAmp);
    const links = graph.allIntraLinks.filter(link => link.nodeId === node.id);
    fuses.push({ rating: node.fuseAmp, ends: [...new Set(links.flatMap(link => [v(node.id, link.from), v(node.id, link.to)]))],
      cuts: edge => edge.kind === "intra" && edge.link.nodeId === node.id,
      cableIds: system.cables.filter(cable => cable.fromNodeId === node.id || cable.toNodeId === node.id).map(cable => cable.id), nodeIds: [node.id] });
  }
  for (const cable of system.cables) {
    const rating = cable.fuseAmpOverride;
    if (rating == null || !Number.isFinite(rating) || rating <= 0) continue;
    // Mirroring a holder's rating on its cable still describes one fuse.
    if (holders.get(cable.fromNodeId) === rating || holders.get(cable.toNodeId) === rating) continue;
    fuses.push({ rating, ends: [v(cable.fromNodeId, cable.fromTerminalId), v(cable.toNodeId, cable.toTerminalId)],
      cuts: edge => edge.kind === "cable" && edge.cableId === cable.id, cableIds: [cable.id], nodeIds: [] });
  }

  const issues: Issue[] = [];
  for (const fuse of fuses) {
    const string = new Set<Panel>();
    for (const end of fuse.ends) {
      const vertices = region(end, fuse.cuts);
      // A parallel copper path around the fuse means it is not in series.
      if (fuse.ends.some(other => other !== end && vertices.has(other))) continue;
      if ([...vertices].some(vertex => converterTerminals.has(vertex))) continue;
      const terminals = terminalsIn(vertices);
      // More than one panel terminal on this side marks a parallel junction.
      if (terminals.length !== 1) continue;
      const queue = [terminals[0].panel];
      for (let i = 0; i < queue.length; i++) {
        const panel = queue[i];
        if (string.has(panel)) continue;
        string.add(panel);
        queue.push(...series.get(panel)!);
      }
    }
    const limiting = [...string].filter(panel => limitOf(panel) < fuse.rating).sort((a, b) => limitOf(a) - limitOf(b));
    if (limiting.length === 0) continue;
    const panel = limiting[0], max = limitOf(panel);
    const label = panel.node.label ?? panel.def.shortName ?? panel.def.name;
    issues.push({ code: "pv-string-fuse-exceeds-module", severity: "critical",
      title: { key: "diag.pv-string-fuse-exceeds-module.title", vars: { fuse: fuse.rating, max } },
      message: { key: "diag.pv-string-fuse-exceeds-module.message", vars: { label, fuse: fuse.rating, max } },
      fix: { key: "diag.pv-string-fuse-exceeds-module.fix", vars: { max } },
      nodeIds: [...fuse.nodeIds, ...limiting.map(item => item.node.id)], cableIds: fuse.cableIds,
    });
  }
  return issues;
}
